import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchTripTimeline } from "../../store/Slices/TripSlice";
import "../../styles/Trip.css";
import { activeLiveTripIcon, arrowIcon, completedTripIcon, historyTripIcon, liveTripIcon, locationIcon, overSpeedIcon, pauseTripIcon, speedIcon, transporterIcon, tripDrivingIcon, tripStartedIcon, tripStoppedIcon, vehicleIcon } from "../../utils/svgs";
import UserAvatar from "../../components/UserAvatar";
import { images } from "../../utils/images";

const TripTracker = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { tripTimelineData, tripDetailsData, timelineLoading } = useSelector(
        (state) => state.trips
    );

    useEffect(() => {
        if (id) {
            dispatch(fetchTripTimeline(id));
        }
    }, [id, dispatch]);

    const formatTime = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    // Helper to map event types to UI Logic
    const getEventConfig = (event) => {
        const type = event.type || "";
        const status = event.status || "";

        let title = type;
        let icon = activeLiveTripIcon; // Default
        let colorClass = "status_default"; // Blue

        if (type === "trip_start") {
            title = "Trip Started";
            icon = images.tripStartedIcon;
            colorClass = "status_start";
        }
        else if (type === "trip_created") {
            title = "Trip Created";
            icon = images.tripStartedIcon;
            colorClass = "status_created";
        }
        else if (type === "trip_end") {
            title = "Trip Ended";
            icon = images.trip_end;
            colorClass = "status_stop";
        }
        else if (type === "vehicle_status") {
            if (status === "RUNNING") {
                title = "Driving";
                icon = images.drivingIcon;
                colorClass = "status_driving";
            } else if (status === "IDLE") {
                title = "Idle";
                icon = images.idleIcon;
                colorClass = "status_pause";
            } else if (status === "STOP") {
                title = "Stopped";
                icon = images.stopIcon;
                colorClass = "status_stop";
            }
        }
        else if (type === "over_speed") {
            title = "Over Speed";
            icon = images.overSpeedIcon;
            colorClass = "status_over_speed"; // Red for alert
        }
        else if (type === "STOP") {
            title = "Stopped";
            icon = images.stopIcon;
            colorClass = "status_stop";
        }
        else if (type === "completed") {
            title = "Completed";
            icon = images.completedTripIcon;
            colorClass = "status_completed";
        }
        else if (type === "trip_driver_completed") {
            title = "Driver Completed";
            icon = completedTripIcon;
            colorClass = "status_completed";
        }

        return { title, icon, colorClass };
    };

    return (
        <div className="trip_tracker_container">
            {/* Horizontal Scrollable Timeline */}
            <div className="timeline_scroll_wrapper">
                <div className="timeline_track">
                    {tripTimelineData && tripTimelineData.length > 0 ? (
                        tripTimelineData.map((event, index) => {
                            const { title, colorClass } = getEventConfig(event);
                            const isLast = index === tripTimelineData.length - 1;

                            return (
                                <div key={index} className="timeline_step_wrapper">
                                    <div className={`timeline_step ${colorClass}`}>
                                        <div className="step_icon_circle">
                                            {colorClass === 'status_pause' ? (
                                                <span className="step_inner_icon pause_icon">{pauseTripIcon}</span>
                                            ) : colorClass === 'status_start' || colorClass === 'status_created' ? (
                                                <span className="step_inner_icon">{tripStartedIcon}</span>
                                            ) : colorClass === 'status_stop' ? (
                                                <span className="step_inner_icon">{tripStoppedIcon}</span>
                                            ) : colorClass === 'status_completed' ? (
                                                <span className="step_inner_icon">{completedTripIcon}</span>
                                            ) : colorClass === 'status_over_speed' ? (
                                                <span className="step_inner_icon">{overSpeedIcon}</span>
                                            ) : (
                                                <span className="step_inner_icon">{tripDrivingIcon}</span>
                                            )
                                            }
                                        </div>
                                        {!isLast && <div className="step_connector"></div>}
                                    </div>

                                    <div className="step_info">
                                        <p className="step_title">{title}</p>
                                        <p className="step_time">
                                            {formatDate(event.created_at)} {formatTime(event.created_at)}
                                        </p>
                                        {event.location && (
                                            <p className="step_location">{event.location}</p>
                                        )}

                                        <div className="step_tags">
                                            {event.speed > 0 && (
                                                <span className="step_tag speed_tag">
                                                    {speedIcon} {Math.round(event.speed)} km/h
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="no_timeline_data">No timeline data available.</div>
                    )}
                </div>
            </div>

            {/* Trip Summary Section */}
            <div className="tracker_summary_section">
                <div className="summary_card location_card">
                    <div className="location_icon_box">
                        {locationIcon}
                    </div>
                    <div className="location_details">
                        <h6>{tripDetailsData?.dispatch_point || "Start Location"}</h6>
                        <p>{tripDetailsData?.start_at ? new Date(tripDetailsData?.start_at).toLocaleString() : "--"}</p>
                    </div>
                </div>

                <div className="summary_arrow">
                    {arrowIcon}
                </div>

                <div className="summary_card location_card">
                    <div className="location_icon_box">
                        {locationIcon}
                    </div>
                    <div className="location_details">
                        <h6>{tripDetailsData?.decanting_point || "End Location"}</h6>
                        <p>{tripDetailsData?.expected_end_at ? new Date(tripDetailsData?.expected_end_at).toLocaleString() : "--"}</p>
                    </div>
                </div>

                <div className="summary_card driver_card">
                    <div className="driver_info">
                        <div className="driver_img">
                            <UserAvatar username={tripDetailsData?.primary_driver?.username || "Driver Name"} image={tripDetailsData?.primary_driver?.image} />
                        </div>
                        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                            <h6>{tripDetailsData?.primary_driver?.username || "Driver Name"}</h6>
                            <p>(Driver)</p>
                        </div>
                    </div>
                    <div className="vehicle_info">
                        <div className="vehicle_icon">
                            <img src={images.vehcileImg} alt="vehicle" />
                        </div>
                        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                            <h6>{tripDetailsData?.vehicle?.registration_number || "Vehicle"}</h6>
                            <p>(Registration Number)</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TripTracker;
